import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Avatar,
  LinearProgress,
} from '@mui/material';

import {
  Visibility,
  Refresh,
  FilterList,
  School,
  Timer,
  Grade,
  Assessment,
  CheckCircle,
  Cancel,
  PlayArrow,
  Pause,
  Stop,
  ExpandMore,
  Person,
  Subject,
  TrendingUp,
  Schedule,
  Analytics,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { adminService, AdminSession } from '../services/adminService';

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SessionCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const StatusChip = styled(Chip)<{ status: string }>`
  font-weight: 600;
  ${({ status }) => {
    switch (status) {
      case 'active':
        return 'background-color: #4caf50; color: white;';
      case 'completed':
        return 'background-color: #2196f3; color: white;';
      case 'paused':
        return 'background-color: #ff9800; color: white;';
      case 'abandoned':
        return 'background-color: #f44336; color: white;';
      default:
        return 'background-color: #757575; color: white;';
    }
  }}
`;

const AdminSessions: React.FC = () => {
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSessions, setTotalSessions] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    subject: '',
    grade: '',
    sortBy: 'startTime',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminService.getSessions({
        page: page + 1,
        limit: rowsPerPage,
        status: filters.status || undefined,
        subject: filters.subject || undefined,
        grade: filters.grade ? parseInt(filters.grade) : undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
      
      setSessions(response.data.sessions);
      setTotalSessions(response.data.pagination.totalSessions);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId: string) => {
    try {
      setDetailsLoading(true);
      const response = await adminService.getSession(sessionId);
      setSessionDetails(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch session details');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [page, rowsPerPage, filters]);

  const handleViewDetails = (session: AdminSession) => {
    setSelectedSession(session);
    setDetailsOpen(true);
    fetchSessionDetails(session._id);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSession(null);
    setSessionDetails(null);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      subject: '',
      grade: '',
      sortBy: 'startTime',
      sortOrder: 'desc',
    });
    setPage(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <PlayArrow />;
      case 'completed':
        return <CheckCircle />;
      case 'paused':
        return <Pause />;
      case 'abandoned':
        return <Stop />;
      default:
        return <Assessment />;
    }
  };

  const getSubjectColor = (subject: string) => {
    const colors: {[key: string]: string} = {
      math: '#2196F3',
      science: '#4CAF50',
      english: '#FF9800',
      gk: '#9C27B0',
    };
    return colors[subject] || '#757575';
  };

  const formatDuration = (durationMs: number | null | undefined) => {
    if (!durationMs) return 'N/A';
    const minutes = Math.floor(durationMs / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && sessions.length === 0) {
    return (
      <StyledCard>
        <CardContent sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading sessions...
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters Panel */}
      <Accordion expanded={true} onChange={() => {}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Filter Sessions
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Box sx={{ minWidth: 200 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="abandoned">Abandoned</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={filters.subject}
                    label="Subject"
                    onChange={(e) => handleFilterChange('subject', e.target.value)}
                  >
                    <MenuItem value="">All Subjects</MenuItem>
                    <MenuItem value="math">Math</MenuItem>
                    <MenuItem value="science">Science</MenuItem>
                    <MenuItem value="english">English</MenuItem>
                    <MenuItem value="gk">General Knowledge</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Grade"
                  value={filters.grade}
                  onChange={(e) => handleFilterChange('grade', e.target.value)}
                  type="number"
                  inputProps={{ min: 1, max: 12 }}
                />
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filters.sortBy}
                    label="Sort By"
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    <MenuItem value="startTime">Start Time</MenuItem>
                    <MenuItem value="endTime">End Time</MenuItem>
                    <MenuItem value="score">Score</MenuItem>
                    <MenuItem value="duration">Duration</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchSessions}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Sessions Table */}
      <StyledCard>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: getSubjectColor(session.subject), width: 32, height: 32 }}>
                        {session.student.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {session.student.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.student.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={session.subject.toUpperCase()}
                      size="small"
                      sx={{ bgcolor: getSubjectColor(session.subject), color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={`Grade ${session.grade}`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      status={session.status}
                      label={session.status}
                      size="small"
                      icon={getStatusIcon(session.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {session.score}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(session.correctAnswers + session.incorrectAnswers) / session.totalQuestions * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {session.correctAnswers + session.incorrectAnswers}/{session.totalQuestions}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDuration(session.duration)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(session.startTime)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(session)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalSessions}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </StyledCard>

      {/* Session Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Session Details
            </Typography>
            {selectedSession && (
              <StatusChip
                status={selectedSession.status}
                label={selectedSession.status}
                size="small"
                icon={getStatusIcon(selectedSession.status)}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading session details...
              </Typography>
            </Box>
          ) : selectedSession && sessionDetails ? (
            <Box>
              {/* Session Overview */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Student Information
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        <Avatar sx={{ bgcolor: getSubjectColor(selectedSession.subject) }}>
                          {selectedSession.student.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {selectedSession.student.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedSession.student.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Grade {selectedSession.student.grade}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Test Information
                      </Typography>
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Subject:</Typography>
                          <Chip
                            label={selectedSession.subject.toUpperCase()}
                            size="small"
                            sx={{ bgcolor: getSubjectColor(selectedSession.subject), color: 'white' }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Score:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {selectedSession.score}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Accuracy:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {selectedSession.accuracyRate}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Duration:</Typography>
                          <Typography variant="body2">
                            {formatDuration(selectedSession.duration)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Performance Analytics */}
              {selectedSession.analytics && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Analytics
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={600}>
                          {Math.round(selectedSession.analytics.averageTimePerQuestion)}s
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg Time/Question
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={600}>
                          {selectedSession.analytics.fastestAnswer}s
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fastest Answer
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={600}>
                          {selectedSession.analytics.slowestAnswer}s
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Slowest Answer
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight={600}>
                          {Math.round(selectedSession.currentDifficultyLevel * 100)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Final Difficulty
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Question Responses */}
              {sessionDetails.responses && sessionDetails.responses.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Question Responses ({sessionDetails.responses.length})
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      {sessionDetails.responses.map((response: any, index: number) => (
                        <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2" fontWeight={500}>
                              Question {index + 1}
                            </Typography>
                            <Box display="flex" gap={1}>
                              <Chip
                                label={response.question.topic}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={response.isCorrect ? 'Correct' : 'Incorrect'}
                                size="small"
                                color={response.isCorrect ? 'success' : 'error'}
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {response.question.questionText}
                          </Typography>
                          <Box display="flex" gap={2} sx={{ fontSize: '0.875rem' }}>
                            <Typography variant="body2" color="text.secondary">
                              Student Answer: <strong>{response.studentAnswer}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Time: {response.timeTaken}s
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSessions; 